import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDownIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { getButtonStyles } from './Button';
import {
  getIconButtonStyles,
  IconButtonIcon,
  IconButtonSizeMap,
  IconButtonTypeMap,
} from './IconButton';
import {
  getIconButtonInvertedStyles,
  IconButtonInvertedIcon,
} from './IconButtonInverted';
import EllipsisVerticalLargeIcon from '../icons/vertical-dots-large.svg?react';

interface Props<T> {
  options: Array<{ label: string; value: T; danger?: boolean }>;
  onOptionSelect: (value: T) => void;
  size: IconButtonSizeMap;
  type: IconButtonTypeMap;
  disabled?: boolean;
  title?: string;
  inverted?: boolean;
  label?: string;
  grow?: boolean;
}

export function ContextMenu<T>({
  options,
  onOptionSelect,
  size,
  type,
  disabled,
  title,
  inverted,
  label,
  grow,
}: Props<T>) {
  const styles: { className: string; style: { padding: string } } = inverted
    ? getIconButtonInvertedStyles({ type, size, disabled: !!disabled })
    : getIconButtonStyles({ type, size, disabled: !!disabled });


  const labelStyles = getButtonStyles({
    type: 'low-priority',
    size: 'xl',
    additionalClassnames: grow ? 'w-full' : 'w-fit',
  });

  return (
    <Menu>
      {label ? (
        <MenuButton
          as="button"
          disabled={!!disabled}
          className={labelStyles.className}
          style={labelStyles.style}
        >
          <span>{label}</span>
          <ChevronDownIcon className="w-4.5 h-4.5 shrink-0" aria-hidden="true" />
        </MenuButton>
      ) : (
        <MenuButton
          as="button"
          className={styles.className}
          style={styles.style}
          disabled={!!disabled}
        >
          {inverted ? (
            <IconButtonInvertedIcon
              size={size}
              Icon={
                type === 'basic'
                  ? EllipsisVerticalLargeIcon
                  : EllipsisVerticalIcon
              }
            />
          ) : (
            <IconButtonIcon
              size={size}
              type={type}
              iconClassName=""
              Icon={EllipsisVerticalIcon}
            />
          )}
        </MenuButton>
      )}
      <MenuItems
        anchor="bottom start"
        className={classNames(
          'border rounded-xs min-w-(--button-width) z-60 outline-none',
          inverted
            ? 'bg-white border-black-t-800 text-[#252525]'
            : 'bg-background-tertiary border-grey-t-800 text-white'
        )}
      >
        {title && (
          <div className="p-4 pb-2 font-bold">
            <h4 className="text-xl font-grotesque">{title}</h4>
          </div>
        )}
        <div className="p-1 space-y-1 flex flex-col">
          {options.map((opt) => (
            <MenuItem key={opt.label}>
              <button
                className={classNames(
                  'z-60 px-3 py-2.5 text-left rounded-xs text-sm',
                  inverted ? 'hover:bg-black-t-900' : 'hover:bg-grey-t-800',
                  opt.danger && (inverted ? 'text-red-600' : 'text-red-400')
                )}
                onClick={() => onOptionSelect(opt.value)}
              >
                {opt.label}
              </button>
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
}
